import React, { forwardRef, useImperativeHandle, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { heicTo } from 'heic-to/csp';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heics', 'image/heif'];
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.heics', '.heif'];

export interface IUploadFromDeviceRef {
  open: () => void;
}

interface IUploadFromDeviceProps {
  onUpload: (src: string) => void;
}

const UploadFromDevice = forwardRef<IUploadFromDeviceRef, IUploadFromDeviceProps>(({ onUpload }, ref) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    open: () => fileInputRef.current?.click(),
  }));

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      const isValidType = ALLOWED_IMAGE_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(extension);

      if (!isValidType) {
        enqueueSnackbar(t('capturePhoto.errors.invalidFileType'), { variant: 'error' });
        event.target.value = '';
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        enqueueSnackbar(t('capturePhoto.errors.fileSizeExceeded'), { variant: 'error' });
        event.target.value = '';
        return;
      }

      event.target.value = '';

      const isHeic = /\.(heic|heif|heics)$/i.test(file.name) || /image\/(heic|heif)/i.test(file.type);

      if (!isHeic) {
        const reader = new FileReader();
        reader.onloadend = () => onUpload(reader.result as string);
        reader.readAsDataURL(file);
        return;
      }

      // Primary: heic-to (WASM libheif — browser-agnostic)
      try {
        const convertedBlob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.8 });
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(convertedBlob);
        });
        onUpload(base64);
        return;
      } catch (e1) {
        console.error('heic-to failed:', e1);
      }

      // Fallback 1: <img> element (works in Safari for all HEIC variants)
      try {
        const base64 = await new Promise<string>((resolve, reject) => {
          const url = URL.createObjectURL(file);
          const img = new Image();
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.naturalWidth;
              canvas.height = img.naturalHeight;
              canvas.getContext('2d').drawImage(img, 0, 0);
              URL.revokeObjectURL(url);
              resolve(canvas.toDataURL('image/jpeg', 0.8));
            } catch (e) {
              URL.revokeObjectURL(url);
              reject(e);
            }
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('img decode failed'));
          };
          img.src = url;
        });
        onUpload(base64);
        return;
      } catch (e2) {
        console.error('img fallback failed:', e2);
      }

      // Fallback 2: createImageBitmap (Chrome with OS HEVC codec)
      try {
        const bitmap = await createImageBitmap(file);
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        bitmap.close();
        onUpload(canvas.toDataURL('image/jpeg', 0.8));
        return;
      } catch (e3) {
        console.error('createImageBitmap fallback failed:', e3);
      }

      enqueueSnackbar(t('capturePhoto.errors.conversionFailed'), { variant: 'error' });
    },
    [t, onUpload],
  );

  return (
    <input
      ref={fileInputRef}
      type="file"
      accept={[...ALLOWED_IMAGE_TYPES, ...ALLOWED_EXTENSIONS].join(',')}
      style={{ display: 'none' }}
      data-testid="profile-picture-file-input"
      onChange={handleFileSelect}
    />
  );
});

UploadFromDevice.displayName = 'UploadFromDevice';

export default UploadFromDevice;
